"use client";

import { useEffect, useCallback, useRef } from "react";
import Script from "next/script";
import { useTrackingConsent } from "@/lib/consent/use-tracking-consent";
import { isTrackingAllowed } from "@/lib/consent/privacy";
import { usePathname } from "next/navigation";
import { X_PIXEL } from "@/lib/analytics/config";
import {
  trackXPixelLead,
  trackXPixelDownload,
  trackXPixelAddToCart,
  trackXPixelPurchase,
} from "@/lib/analytics/events";
import type { ConsentUpdateEvent } from "@/lib/analytics/types";

export default function XPixelTracker() {
  const allowed = useTrackingConsent();
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  // SPA pageview fan-out. The initial PageView fires when twq('config') runs
  // on script load — this effect only fires on subsequent App Router navs,
  // mirroring PostHogPageView + the Meta tracker's SPA effect.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isTrackingAllowed()) return;
    if (typeof window === "undefined" || typeof window.twq !== "function") return;
    if (window.__xPixelBlocked) return;
    try {
      window.twq("track", "PageView");
    } catch (e) {
      console.warn("X pixel SPA PageView failed:", e);
    }
  }, [pathname]);

  // Listen for consent changes and update pixel accordingly
  const handleConsentUpdate = useCallback((event: ConsentUpdateEvent) => {
    if (window.__xPixelInitialized && window.twq) {
      try {
        const newState = event.detail.state;
        if (newState === "granted" && isTrackingAllowed()) {
          window.twq("config", X_PIXEL.PIXEL_ID, {
            opt_out: false,
          });
          console.log("X pixel enabled after consent");
        } else {
          window.twq("config", X_PIXEL.PIXEL_ID, {
            opt_out: true,
          });
          console.log("X pixel disabled after decline");
        }
      } catch (e) {
        console.error("Error updating X pixel consent:", e);
      }
    }
  }, []);

  useEffect(() => {
    // Add consent update listener
    window.addEventListener("consent_update", handleConsentUpdate as EventListener);

    // Define tracking methods globally for easier access
    // Use the centralized tracking functions from events.ts
    window.xPixelTrack = {
      trackLead: trackXPixelLead,
      trackDownload: trackXPixelDownload,
      trackAddToCart: trackXPixelAddToCart,
      trackPurchase: trackXPixelPurchase,
    };

    // Cleanup event listener
    return () => {
      window.removeEventListener("consent_update", handleConsentUpdate as EventListener);
    };
  }, [handleConsentUpdate]);

  if (!allowed) return null;

  return (
    <>
      {/* Twitter/X pixel script with consent check */}
      <Script
        id="twitter-pixel"
        strategy="lazyOnload"
        onError={(error) => {
          console.warn("X pixel script failed to load (likely blocked by adblocker):", error);
          window.__xPixelBlocked = true;
        }}
      >
        {`
          (function () {
          if (!window.privacyConsent?.allowed) return;
          try {
            // Initialize the Twitter pixel directly
              !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
              },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
              a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');

              // Wait for twq to be ready with timeout and error handling
              let waitAttempts = 0;
              const maxWaitAttempts = 30; // 3 seconds max wait

              const waitForTwq = setInterval(function() {
                waitAttempts++;

                if (typeof window.twq === 'function') {
                  clearInterval(waitForTwq);

                  try {
                    // Set global flag to track initialization
                    window.__xPixelInitialized = true;

                    // Recheck the shared decision in case consent changed during load.
                    window.twq('config', '${X_PIXEL.PIXEL_ID}', {
                      'opt_out': !window.privacyConsent?.allowed
                    });

                    console.log('X pixel initialized under shared privacy consent');

                  } catch (configError) {
                    console.warn('X pixel configuration failed:', configError);
                    window.__xPixelBlocked = true;
                  }
                } else if (waitAttempts >= maxWaitAttempts) {
                  clearInterval(waitForTwq);
                  console.warn('X pixel failed to initialize within timeout (likely blocked)');
                  window.__xPixelBlocked = true;
                }
              }, 100);

            // Set a timeout to mark as blocked if no response within 5 seconds
            setTimeout(function() {
              if (!window.__xPixelInitialized && !window.__xPixelBlocked) {
                console.warn('X pixel initialization timeout - likely blocked');
                window.__xPixelBlocked = true;
              }
            }, 5000);

          } catch (error) {
            console.warn('X pixel initialization failed:', error);
            window.__xPixelBlocked = true;
          }
          })();
        `}
      </Script>
    </>
  );
}
